/**
 * ERG low-cadence protection (SPEC §6.4):
 * cadence < 50 rpm for 3 s → ramp the target down to 50 %; once cadence > 70 rpm, restore over 10 s.
 */
export interface ErgProtectionConfig {
  lowRpm: number;
  lowHoldSec: number;
  floorFactor: number;
  rampDownSec: number;
  restoreRpm: number;
  restoreSec: number;
}

export const DEFAULT_ERG_PROTECTION: ErgProtectionConfig = {
  lowRpm: 50,
  lowHoldSec: 3,
  floorFactor: 0.5,
  rampDownSec: 2,
  restoreRpm: 70,
  restoreSec: 10,
};

export class ErgProtection {
  private lowFor = 0;
  private factorValue = 1;
  private reducing = false;

  constructor(private readonly cfg: ErgProtectionConfig = DEFAULT_ERG_PROTECTION) {}

  get factor(): number {
    return this.factorValue;
  }

  get active(): boolean {
    return this.factorValue < 0.999;
  }

  /** Advance by dt seconds with the current cadence. Returns the multiplier for the ERG target. */
  update(dtSec: number, cadenceRpm: number): number {
    const c = this.cfg;
    if (cadenceRpm < c.lowRpm) {
      this.lowFor += dtSec;
      if (this.lowFor >= c.lowHoldSec) this.reducing = true;
    } else {
      this.lowFor = 0;
    }
    if (this.reducing && cadenceRpm > c.restoreRpm) this.reducing = false;

    if (this.reducing) {
      const rate = (1 - c.floorFactor) / c.rampDownSec;
      this.factorValue = Math.max(c.floorFactor, this.factorValue - rate * dtSec);
    } else if (this.factorValue < 1 && cadenceRpm > c.restoreRpm) {
      const rate = (1 - c.floorFactor) / c.restoreSec;
      this.factorValue = Math.min(1, this.factorValue + rate * dtSec);
    }
    return this.factorValue;
  }

  reset(): void {
    this.lowFor = 0;
    this.factorValue = 1;
    this.reducing = false;
  }
}
